import { marked } from "marked";
import siteBase from "@/data/site-base.json";

export interface BlogFrontmatter {
  date?: string;
  summary?: string;
}

export interface BlogPost {
  filename: string;
  slug: string;
  title: string;
  summary: string;
  date: string | null;
  content: string;
  rawBody: string;
  anchor: string;
}

const FRONTMATTER_RE = /^---\r?\n([\s\S]*?)\r?\n---\r?\n?/;

const parseYamlLine = (line: string): [string, string] | null => {
  const match = line.match(/^([A-Za-z0-9_-]+):\s*(.+)$/);
  if (!match) return null;
  let value = match[2].trim();
  if (
    (value.startsWith('"') && value.endsWith('"')) ||
    (value.startsWith("'") && value.endsWith("'"))
  ) {
    value = value.slice(1, -1);
  }
  return [match[1], value];
};

export const parseFrontmatter = (
  text: string,
): { frontmatter: BlogFrontmatter; body: string } => {
  const match = text.match(FRONTMATTER_RE);
  if (!match) return { frontmatter: {}, body: text };

  const frontmatter: BlogFrontmatter = {};
  for (const line of match[1].split(/\r?\n/)) {
    const parsed = parseYamlLine(line.trim());
    if (!parsed) continue;
    const [key, value] = parsed;
    if (key === "date") frontmatter.date = value;
    if (key === "summary") frontmatter.summary = value;
  }

  return { frontmatter, body: text.slice(match[0].length) };
};

export const slugFromFilename = (filename: string): string =>
  filename.replace(/\.md$/, "");

export const extractBlogTitle = (markdownText: string, filename: string): string => {
  const titleMatch = markdownText.match(/^#\s+(.+)$/m);
  if (titleMatch) return titleMatch[1].trim();
  return slugFromFilename(filename)
    .replace(/([A-Z])/g, " $1")
    .trim();
};

export const stripMarkdown = (text: string): string =>
  text
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/`[^`]+`/g, " ")
    .replace(/!\[.*?\]\(.*?\)/g, " ")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/[*_~>#-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

export const sortBlogPosts = (posts: BlogPost[]): BlogPost[] =>
  [...posts].sort((a, b) => {
    if (!a.date && !b.date) return 0;
    if (!a.date) return 1;
    if (!b.date) return -1;
    return b.date.localeCompare(a.date);
  });

export const fetchBlogPost = async (filename: string): Promise<BlogPost | null> => {
  try {
    const response = await fetch(`/blog/${filename}`);
    if (!response.ok) return null;

    const text = await response.text();
    const { frontmatter, body } = parseFrontmatter(text);
    const title = extractBlogTitle(body, filename);
    const content = await marked.parse(body);
    const slug = slugFromFilename(filename);

    return {
      filename,
      slug,
      title,
      summary: frontmatter.summary || stripMarkdown(body).slice(0, 160),
      date: frontmatter.date || null,
      content,
      rawBody: body,
      anchor: slug,
    };
  } catch (err) {
    console.error(`Error loading ${filename}:`, err);
    return null;
  }
};

export const blogFiles: string[] = siteBase.blogFiles;

export const fetchAllBlogPosts = async (
  filenames: string[] = blogFiles,
): Promise<BlogPost[]> => {
  const posts = await Promise.all(filenames.map(fetchBlogPost));
  return sortBlogPosts(posts.filter((post): post is BlogPost => post !== null));
};

export const formatBlogDate = (date: string | null, locale: string): string | null => {
  if (!date) return null;
  const parsed = new Date(`${date}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return date;
  return parsed.toLocaleDateString(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};
